import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg';
import { useEffect, useState } from 'react';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import usePlanStore from '../../../../../../store/planStore';
import { Countries } from '../../../../../../shared/enums';

interface CountryListProps {
  setTabToRemove: (tab: string | null) => void;
}

export default function CountryList({ setTabToRemove }: CountryListProps) {
  const {
    selectedCountry,
    setSelectedCountry,
    addCountry,
    updateCountryForm,
    countryForms,
    setActiveNestedTab,
    setSelectedCountryISOCode,
    storedCountryForms,
    isLocked,
  } = usePlanStore((state) => ({
    selectedCountry: state.selectedCountry,
    setSelectedCountry: state.setSelectedCountry,
    addCountry: state.addCountry,
    updateCountryForm: state.updateCountryForm,
    countryForms: state.countryForms,
    setSelectedCountryISOCode: state.setSelectedCountryISOCode,
    setActiveNestedTab: state.setActiveNestedTab,
    storedCountryForms: state.storedCountryForms,
    isLocked: state.isLocked,
  }));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const availableCountries = Object.keys(Countries).filter(
    (country) => !countryForms[country]
  );

  const handleOpenDropdown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  const canRemoveCountry = (country: string) =>
    !isLocked &&
    storedCountryForms[country]?.metaConfig?.reservationStatus !== 'reserved' &&
    storedCountryForms[country]?.metaConfig?.reservationStatus !==
      'partially-reserved';

  const handleCountrySelect = (country: string) => {
    addCountry(country);
    setActiveNestedTab('digital');
    setSelectedCountry(country);
    setSelectedCountryISOCode(country);

    handleCloseDropdown();
  };

  return (
    <Box
      sx={{
        width: '216px',
        padding: '27px 29px',
        height: '100%',
        minHeight: 'calc(100vh - 230px)',
        background: 'white',
        borderRadius: '5px',
        mt: '16px',
      }}
    >
      <Stack spacing={2}>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          minHeight="40px"
        >
          <Typography
            variant="h6"
            fontSize="14px"
            textTransform="uppercase"
            fontWeight={500}
          >
            Countries
          </Typography>
          {!isLocked && availableCountries.length > 0 && (
            <IconButton onClick={handleOpenDropdown}>
              <PlusIcon />
            </IconButton>
          )}
        </Stack>
        {Object.keys(countryForms).map((country) => (
          <Box
            key={country}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 1,
              outline:
                selectedCountry === country ? '2px solid #437EEB' : 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#fbfafa',
              },
            }}
            onClick={() => setSelectedCountry(country)}
          >
            <Typography
              textTransform="capitalize"
              sx={
                selectedCountry === country
                  ? {
                      color: '#447EEB',
                      fontWeight: 700,
                      fontSize: '16px',
                    }
                  : {
                      color: '#525F81',
                      fontWeight: 500,
                      fontSize: '16px',
                    }
              }
            >
              {country.toLowerCase()}
            </Typography>
            {!isLocked &&
              (canRemoveCountry(country) ? (
                <IconButton
                  sx={{ color: '#F00F00' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTabToRemove(country);
                  }}
                >
                  <CrossIcon />
                </IconButton>
              ) : (
                <Tooltip title="You can't remove country that has Meta booked">
                  <InfoOutlineIcon
                    sx={{ color: '#447EEB', mr: '4px', fontSize: '18px' }}
                  />
                </Tooltip>
              ))}
          </Box>
        ))}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseDropdown}
          PaperProps={{
            style: {
              maxHeight: 300,
              width: '200px',
            },
          }}
        >
          {availableCountries.map((country) => (
            <MenuItem
              key={country}
              onClick={() => handleCountrySelect(country)}
            >
              {country}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    </Box>
  );
}
